import type { FeedingPenOption } from './penTypes';

const SEPARATOR = ' · ';

/**
 * A label per pen that is unique across the whole list.
 *
 * Listing the pens of a period rather than today's pens brings back pens that were
 * replaced under the same name, so two options can read identically. A reused name is
 * told apart by the date the older pen was marked historical, which is what an operator
 * recognises; the id is the last resort, for pens sharing both name and status.
 *
 * A generated label can still equal another pen's own name (a pen literally called
 * "P01 · 2026-04-02"), and the per-pen charts key their data by the label, so a repeat
 * would draw one pen's value under the other. Pens whose name is unique keep it as it
 * is, and a generated label that meets a label already taken gets the pen's id.
 */
export function penDisplayNames(options: FeedingPenOption[]): Map<number, string> {
    const byName = new Map<string, FeedingPenOption[]>();
    for (const option of options) {
        byName.set(option.cageName, [...(byName.get(option.cageName) ?? []), option]);
    }

    const labels = new Map<number, string>();
    const taken = new Set<string>();
    for (const [name, group] of byName) {
        if (group.length === 1) {
            labels.set(group[0].cageId, name);
            taken.add(name);
        }
    }
    for (const [name, group] of byName) {
        if (group.length === 1) continue;
        const sharing = new Map<string | null, number>();
        for (const option of group) {
            sharing.set(option.historicalSince, (sharing.get(option.historicalSince) ?? 0) + 1);
        }
        for (const option of group) {
            const parts = [name];
            if (option.historicalSince !== null) parts.push(option.historicalSince);
            if ((sharing.get(option.historicalSince) ?? 0) > 1) parts.push(`#${option.cageId}`);
            let label = parts.join(SEPARATOR);
            // Each round lengthens the label, and the taken set is finite, so this ends.
            while (taken.has(label)) label = `${label}${SEPARATOR}#${option.cageId}`;
            labels.set(option.cageId, label);
            taken.add(label);
        }
    }
    return labels;
}

/**
 * The pen's label from {@link penDisplayNames}, or its own name when there is none for it.
 *
 * The per-pen charts key their categories by this label, so it has to be the unique one:
 * keyed by a shared name, the chart joins both pens onto one key and draws the second
 * pen's value twice.
 */
export function penLabel(
    names: ReadonlyMap<number, string> | undefined,
    pen: { cageId: number; cageName: string }
): string {
    return names?.get(pen.cageId) ?? pen.cageName;
}
